import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Smartphone, ShoppingCart, TrendingUp, Search, Palette, MessageSquare, Users, Zap, Shield, Clock, CheckCircle, Globe, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const services = [
  {
    icon: ShoppingCart,
    title: 'E-commerce Development',
    description: 'Custom storefronts on HCL Commerce and MedusaJS for B2B and B2C brands, including complex pricing and catalog logic',
  },
  {
    icon: TrendingUp,
    title: 'Custom Software',
    description: 'Integrations between your storefront, ERP, PIM, and fulfillment systems, built for real order volume',
  },
  {
    icon: Code,
    title: 'Website Development',
    description: 'Custom, responsive websites that drive results',
  },
  {
    icon: Smartphone,
    title: 'Mobile App Development',
    description: 'Native and cross-platform mobile solutions',
  },
  {
    icon: Search,
    title: 'SEO Services',
    description: 'Boost your visibility and organic traffic',
  },
  {
    icon: Palette,
    title: 'UI/UX Design',
    description: 'Beautiful interfaces that users love',
  },
  {
    icon: MessageSquare,
    title: 'Digital Marketing',
    description: 'Drive growth with strategic campaigns',
  },
  {
    icon: Users,
    title: 'Consulting',
    description: 'Expert guidance for your digital journey',
  },
];

const benefits = [
  { icon: Zap, title: 'Fast Delivery', description: 'Quick turnaround without compromising quality' },
  { icon: Shield, title: 'Secure Solutions', description: 'Enterprise-grade security in every project' },
  { icon: Clock, title: '24/7 Support', description: 'Responsive support when you need it' },
  { icon: Users, title: 'Direct Senior Engineering', description: 'You work directly with the engineer building your project — no account managers, no junior handoffs.' },
  { icon: Globe, title: 'Real Platform Experience', description: 'Hands-on experience leading engineering on production B2B and B2C e-commerce platforms, including complex pricing, quoting, and integration work.' },
  { icon: Award, title: 'Platform-Agnostic Advice', description: 'We recommend whats right for your scale — not a rewrite just to sell you one.' },
];

const process = [
  { step: '01', title: 'Discovery', description: 'Understanding your vision and requirements' },
  { step: '02', title: 'Planning', description: 'Strategic roadmap and architecture design' },
  { step: '03', title: 'Development', description: 'Building with cutting-edge technologies' },
  { step: '04', title: 'Testing', description: 'Rigorous quality assurance and testing' },
  { step: '05', title: 'Launch', description: 'Smooth deployment and go-live support' },
  { step: '06', title: 'Support', description: 'Ongoing maintenance and improvements' },
];

const featuredProjects = [
  {
    title: 'B2B/B2C Distribution Platform',
    category: 'E-commerce',
    description: 'Led engineering for an e-commerce platform serving a national distributor. Built dealer account hierarchies, custom pricing, and scalable catalog management. Delivered a robust solution optimized for performance and business growth.',
    image: 'https://images.unsplash.com/photo-1557821552-17105176677c?q=80&w=800',
  },
  {
    title: 'Retail E-commerce Replatform',
    category: 'E-commerce',
    description: 'Rebuilt an e-commerce storefront with a focus on performance and scalability. Improved the checkout flow to enhance the user purchase experience and increase conversions.Developed a flexible product configuration system and optimized the catalog.',
    image: 'https://images.ctfassets.net/wt70guc1rpin/wp-media-91761/b36ec7716564488973c26a8f2ff5a953/an-example-of-a-checkout-process-using-an-ecommerc.png',
  },
  {
    title: 'Headless Commerce Migration',
    category: 'E-commerce',
    description: 'Migrated a B2C retail storefront from legacy JSP to headless HCL Commerce with a React frontend. Currently leading the next phase by migrating to Next.js to unify B2B and B2C on a single platform. Improved application performance, maintainability, and scalability.',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=800',
  },
];

export default function HomePage() {
  return (
    <>
      <Helmet>
        <title>FuturePath Technologies - Leading Software Development Company</title>
        <meta name="description" content="Transform your business with cutting-edge software solutions. Web development, mobile apps, e-commerce, and digital marketing services." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:'url(/banner.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold font-outfit tracking-tight mb-6 text-white" data-testid="hero-title">
              E-commerce {' '}
              <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
              Engineering for B2B & B2C Brands
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed" data-testid="hero-subtitle">
              We build and scale commerce platforms on HCL Commerce, MedusaJS, React, and Next.js — for brands whose catalog, pricing, and integrations are too complex for generic web agencies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-primary hover:bg-primary/90 neon-glow text-base px-8"
                data-testid="hero-consultation-btn"
              >
                <Link to="/consultation">
                  Get Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 text-base px-8"
                data-testid="hero-services-btn"
              >
                <Link to="/services">View Our Services</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding relative" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-4 text-white">
              Our Services
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Comprehensive digital solutions tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="glass-card p-6 h-full hover:scale-105 transition-transform duration-300" data-testid={`service-card-${index}`}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-4">
                    <service.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-semibold font-outfit text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {service.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="view-all-services-btn">
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding relative bg-surface/30" data-testid="why-choose-us-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-4 text-white">
              Why Choose FuturePath
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Partner with a team that delivers excellence at every step
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 rounded-3xl"
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6">
                  <benefit.icon size={28} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold font-outfit text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Highlights */}
      <section className="section-padding relative" data-testid="portfolio-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-4 text-white">
              Featured Projects
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Showcasing our best work and successful client collaborations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group"
                data-testid={`project-card-${index}`}
              >
                <div className="glass-card rounded-3xl overflow-hidden hover:scale-105 transition-transform duration-500">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="text-xs text-primary font-mono mb-2">{project.category}</div>
                    <h3 className="text-xl font-semibold font-outfit text-white mb-2">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {project.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* <div className="text-center mt-12">
            <Button asChild size="lg" className="rounded-full bg-primary hover:bg-primary/90 neon-glow" data-testid="view-portfolio-btn">
              <Link to="/portfolio">View All Projects</Link>
            </Button>
          </div> */}
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding relative bg-surface/30" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-4 text-white">
              Our Process
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A proven methodology that ensures project success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
                data-testid={`process-step-${index}`}
              >
                <div className="glass-card p-8 rounded-3xl h-full">
                  <div className="text-5xl font-bold font-outfit text-primary/30 mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-2xl font-semibold font-outfit text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="learn-more-process-btn">
              <Link to="/process">Learn More About Our Process</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative" data-testid="cta-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-12 md:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent" />
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-6 text-white">
                Ready to Build Your Digital Product?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Let's transform your ideas into reality. Schedule a free consultation today and discover how we can help your business thrive.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 neon-glow text-base px-8"
                  data-testid="cta-consultation-btn"
                >
                  <Link to="/consultation">
                    Schedule Consultation
                    <ArrowRight className="ml-2" size={20} />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-full border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 text-base px-8"
                  data-testid="cta-contact-btn"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
